<template>
  <Popup 
    :ref="(el) => popup = el"
    :title="$t('fraudex.check_result')"
    class="popup__lg"
    @onClose="hide"
  >
    <div class="popup__content">
      <div v-if="fraudexCheck">
        <div v-if="fraudexCheck.error" class="alert alert-danger" role="alert">
          <h6 class="alert-heading mb-2">
            <i class="fa fa-exclamation-triangle me-2"></i>
            {{ $t('fraudex.check_error') }}
          </h6>
          <p class="mb-0">
            {{ fraudexCheck.error }}
          </p>
        </div>

        <div v-else>
          <div v-if="fraudexCheck.isBlacklisted === false">
            <div class="alert alert-success" role="alert">
              <h6 class="alert-heading mb-2">
                <i class="fa fa-check-circle me-2"></i>
                {{ $t('fraudex.not_in_blacklist') }}
              </h6>
              <p class="mb-0 small">
                <strong>{{ $t('labels.requisites') }}:</strong> {{ fraudexCheck.requisite }}
              </p>
            </div>
          </div>

          <div v-else-if="fraudexCheck.isBlacklisted === true">
            <div class="alert alert-danger" role="alert">
              <h6 class="alert-heading mb-2">
                <i class="fa fa-exclamation-triangle me-2"></i>
                  {{ $t('fraudex.in_blacklist') }}
              </h6>
              <p class="mb-0 small">
                <strong>{{ $t('labels.requisites') }}:</strong> {{ fraudexCheck.requisite }}
              </p>
            </div>

            <div v-if="fraudexCheck.comments && fraudexCheck.comments.length > 0" class="mb-3 mt-3 fraudex-comments">
              <h6 class="fraudex-comments__title mb-3">{{ $t('fraudex.comments') }}:</h6>
              <ul class="fraudex-comments__list">
                <li v-for="(comment, index) in fraudexCheck.comments" :key="index" class="fraudex-comments__item">
                  {{ comment }}
                </li>
              </ul>
            </div>

            <div class="mb-3">
              <div class="form-check">
                <input 
                  class="form-check-input" 
                  type="checkbox" 
                  v-model="riskConfirmed" 
                  id="riskConfirmation"
                >
                <label class="form-check-label" for="riskConfirmation">
                  {{ $t('fraudex.confirm_risks') }}
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <div class="popup__controls">
      <button 
        type="button" 
        @click="handleCancel" 
        class="btn popup__close"
      >
        <span class="text-capitalize-first">{{ $t('button.cancel') }}</span>
      </button>
      <button 
        type="button" 
        @click="handleContinue" 
        :disabled="fraudexCheck && !fraudexCheck.error && fraudexCheck.isBlacklisted === true && !riskConfirmed"
        class="btn btn--accept"
      >
        <i class="fa fa-check me-2"></i>
        <span class="text-capitalize-first">{{ $t('button.continue') }}</span>
      </button>
    </div>
  </Popup>
</template>

<script lang="ts">
import { defineComponent, ref, watch, PropType } from 'vue';
import { reactive, toRefs } from '@vue/reactivity';
import Popup from '@/apps/public/components/Popup.vue';
import { $t, helper } from '@/service/CommonHelper';
import FraudexCheckResponseDTO from '@/models/FraudexCheckResponseDTO';

export default defineComponent({
  name: 'FraudexCheckPopup',
  methods: { $t: $t },
  components: {
    Popup,
  },
  props: {
    fraudexCheck: {
      type: Object as PropType<FraudexCheckResponseDTO | null>,
      default: null,
    },
  },
  emits: ['continue', 'cancel'],
  setup(props, { emit }) {
    const popup = ref<InstanceType<typeof Popup> | null>(null);
    const riskConfirmed = ref(false);

    const show = () => {
      riskConfirmed.value = false;
      popup.value?.show();
    };

    const hide = () => {
      popup.value?.hide();
    };

    const handleContinue = () => {
      emit('continue');
      hide();
    };

    const handleCancel = () => {
      emit('cancel');
      hide();
    };

    return {
      helper,
      riskConfirmed,
      popup,
      show,
      hide,
      handleContinue,
      handleCancel,
    };
  },
});
</script>

<style scoped>
.alert-heading {
  font-size: 1rem;
}

.fraudex-comments {
  margin-top: 1.5rem;
  margin-bottom: 1.5rem;
}

.fraudex-comments__title {
  font-size: 1.1rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 1rem;
}

.fraudex-comments__list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.fraudex-comments__item {
  padding: 1rem;
  margin-bottom: 0.75rem;
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  font-size: 1rem;
  line-height: 1.5;
  color: #212529;
}

.fraudex-comments__item:last-child {
  margin-bottom: 0;
}
</style>
